/**
 * CL-408O / CL-409J — Flexible project collaborator capability flags.
 */

import { isProjectAdminEquivalentRole } from "./portalInvite";
import {
  resolveCanPublishEstimate as resolveCanPublishEstimateCapability,
  resolvePlanningCapabilities,
} from "./planningCapabilityResolver";
import { buildAccountingAssignmentPreset } from "./projectAccountingPresetModel";
import {
  ACCOUNTING_EXTENDED_PERMISSION_KEYS,
  LEGACY_INVOICE_PERMISSION_KEY,
  stripExtendedPermissionsForGateOffSave,
} from "./projectStaffFinancialPermissionsModel";
import {
  hasExplicitInvoicePermissionKeys,
  normalizeInvoicePermissionsForAssignmentSave,
} from "./projectInvoicePermissionsEditorModel";
import { normalizeBudgetPermissionsForAssignmentSave } from "./projectBudgetPermissionsEditorModel";
import { normalizeSchedulePermissionsForAssignmentSave } from "./projectSchedulePermissionsEditorModel";
import { normalizeSpecificationPermissionsForAssignmentSave } from "./projectSpecificationPermissionsEditorModel";
import { normalizeDesignBoardPermissionsForAssignmentSave } from "./projectDesignBoardPermissionsEditorModel";
import { DESIGN_BOARD_PERMISSION_KEY_LIST } from "./designBoardPermissionKeys";
import {
  normalizeProjectSupplierPermissionsForAssignmentSave,
  CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_KEY,
  CLIENT_SUPPLIER_PRODUCT_DECISION_PERMISSION_KEY,
} from "./projectSupplierPermissionsEditorModel";
import {
  CLIENT_PORTAL_MODULE_PERMISSION_KEY_LIST,
  normalizeClientPortalModulePermissionsForAssignmentSave,
} from "./clientPortalModulePermissionKeys";
import {
  CLIENT_SAFE_INVOICE_PERMISSION_KEY_LIST,
  normalizeClientSafeInvoicePermissionsForAssignmentSave,
} from "./clientSafeInvoicePermissionKeys";
import { projectAccountingPermissionsUiEnabled } from "./projectAccountingPermissionsFeatureGate";
import { buildVisibleProjectPortalTabs } from "./projectPortalTabsModel";

export const COLLABORATOR_PERMISSION_KEYS = Object.freeze([
  "canViewPlanning",
  "canEditScope",
  "canEditEstimate",
  "canPublishEstimate",
  "canViewPhotos",
  "canUploadFiles",
  "canReviewDrawings",
  "canLogTime",
  "canViewTimeLog",
  "canManageInvoices",
  "canViewProjectSuppliers",
  "canManageProjectSuppliers",
  "canViewProjectSpecifications",
  "canManageProjectSpecifications",
  "canManageCollaborators",
]);

export const COLLABORATOR_PERMISSION_GROUPS = Object.freeze([
  {
    label: "Planning",
    keys: ["canViewPlanning", "canEditScope", "canEditEstimate", "canPublishEstimate"],
  },
  {
    label: "Files / Photos",
    keys: ["canViewPhotos", "canUploadFiles"],
  },
  {
    label: "Drawings",
    keys: ["canReviewDrawings"],
  },
  {
    label: "Time",
    keys: ["canLogTime", "canViewTimeLog"],
  },
  {
    label: "Financials",
    keys: ["canManageInvoices"],
  },
  {
    label: "Admin",
    keys: ["canManageCollaborators"],
  },
]);

export const COLLABORATOR_PERMISSION_LABELS = Object.freeze({
  canViewPlanning: "View planning package",
  canEditScope: "Edit scope",
  canEditEstimate: "Edit planning estimate",
  canPublishEstimate: "Publish estimate to client",
  canViewPhotos: "View photos",
  canUploadFiles: "Upload files",
  canReviewDrawings: "Review drawings",
  canLogTime: "Log time",
  canViewTimeLog: "View time log",
  canManageInvoices: "Prepare and edit invoices",
  canViewProjectSuppliers: "View shared trades, suppliers & product selections",
  canManageProjectSuppliers: "Share trades, suppliers & product selections with client",
  canManageCollaborators: "Manage collaborators",
});

/** Optional longer help text shown under a permission checkbox in Admin. */
export const COLLABORATOR_PERMISSION_EXPLANATIONS = Object.freeze({
  canManageInvoices:
    "Can create and edit project invoice drafts and submit them for Admin review. Cannot publish invoices, change payment status or manage company invoicing settings.",
});

export const COLLABORATOR_PERMISSION_PRESET_KEYS = Object.freeze({
  CLIENT: "client",
  ESTIMATOR: "estimator",
  DESIGNER: "designer",
  TRADE: "trade",
  PROJECT_ADMIN: "project_admin",
  ACCOUNTING: "accounting",
  CUSTOM: "custom",
});

export const COLLABORATOR_PERMISSION_PRESET_LABELS = Object.freeze({
  client: "Client",
  estimator: "Estimator",
  designer: "Designer",
  trade: "Trade / Supplier",
  project_admin: "Project Admin",
  accounting: "Accounting",
  custom: "Custom",
});

export const COLLABORATOR_PERMISSION_PRESET_ORDER = Object.freeze([
  COLLABORATOR_PERMISSION_PRESET_KEYS.CLIENT,
  COLLABORATOR_PERMISSION_PRESET_KEYS.ESTIMATOR,
  COLLABORATOR_PERMISSION_PRESET_KEYS.DESIGNER,
  COLLABORATOR_PERMISSION_PRESET_KEYS.TRADE,
  COLLABORATOR_PERMISSION_PRESET_KEYS.PROJECT_ADMIN,
  COLLABORATOR_PERMISSION_PRESET_KEYS.ACCOUNTING,
  COLLABORATOR_PERMISSION_PRESET_KEYS.CUSTOM,
]);

const ALL_TRUE = Object.freeze(
  COLLABORATOR_PERMISSION_KEYS.reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {})
);

const ALL_FALSE = Object.freeze(
  COLLABORATOR_PERMISSION_KEYS.reduce((acc, key) => {
    acc[key] = false;
    return acc;
  }, {})
);

export const FULL_PROJECT_REQUIRED_PERMISSION_KEYS = Object.freeze([
  "canViewPlanning",
  "canEditScope",
  "canEditEstimate",
  "canPublishEstimate",
  "canViewPhotos",
  "canLogTime",
  "canViewTimeLog",
  "canManageInvoices",
  "canManageCollaborators",
]);

function safeStr(value) {
  return String(value ?? "").trim().toLowerCase();
}

export function isFullProjectAccessScope(accessScope) {
  return safeStr(accessScope || "full_project") === "full_project";
}

function rawStr(value) {
  return String(value ?? "").trim();
}

export function normalizeCollaboratorEmailLower(value) {
  return rawStr(value).toLowerCase();
}

export function findCollaboratorRowForViewer(project = null, { authUid = "", authEmail = "" } = {}) {
  const uid = rawStr(authUid);
  const emailLower = normalizeCollaboratorEmailLower(authEmail);
  const rows = Array.isArray(project?.collaborators) ? project.collaborators : [];
  return (
    rows.find((row) => {
      if (rawStr(row?.status).toLowerCase() === "removed") return false;
      const rowUid = rawStr(row?.uid);
      const rowAuthUid = rawStr(row?.authUid);
      const rowEmail = normalizeCollaboratorEmailLower(row?.emailLower || row?.email);
      return (
        (uid && (rowUid === uid || rowAuthUid === uid)) ||
        (emailLower && rowEmail && rowEmail === emailLower)
      );
    }) || null
  );
}

function isClientRole(role) {
  return safeStr(role) === "client";
}

function isDesignerRole(role) {
  const key = safeStr(role);
  return key === "designer";
}

function isTradeSupplierRole(role) {
  const key = safeStr(role);
  return key === "supplier" || key === "contractor" || key === "trade" || key === "subtrade";
}

function isEstimatorRole(role) {
  const key = safeStr(role);
  return (
    key === "consultant" ||
    key === "reviewer" ||
    key === "collaborator" ||
    key === "internal"
  );
}

function isEngineerRole(role) {
  return safeStr(role) === "engineer";
}

export function buildDefaultCollaboratorPermissions({ role = "reviewer", accessScope = "full_project" } = {}) {
  const fullProjectScope = isFullProjectAccessScope(accessScope);
  if (isProjectAdminEquivalentRole(role) || safeStr(role) === "internal") {
    return fullProjectScope
      ? { ...ALL_TRUE }
      : { ...ALL_FALSE, canUploadFiles: true, canReviewDrawings: true };
  }

  if (isClientRole(role)) {
    return {
      ...ALL_FALSE,
      canViewPlanning: true,
      canViewPhotos: true,
      canReviewDrawings: safeStr(accessScope) !== "shared_files",
      canUploadFiles: false,
    };
  }

  if (isDesignerRole(role)) {
    const defaults = {
      ...ALL_FALSE,
      canViewPlanning: true,
      canViewPhotos: true,
      canUploadFiles: true,
      canReviewDrawings: true,
      canLogTime: true,
    };
    return fullProjectScope ? defaults : { ...ALL_FALSE, canUploadFiles: true, canReviewDrawings: true };
  }

  if (isTradeSupplierRole(role)) {
    const defaults = {
      ...ALL_FALSE,
      canViewPlanning: true,
      canViewPhotos: true,
      canReviewDrawings: false,
    };
    return fullProjectScope ? defaults : { ...ALL_FALSE, canReviewDrawings: true };
  }

  // CL-ENG-INVITE-001 — Engineer: review/comment/upload; no estimate edit, publish, or Time Log by default.
  if (isEngineerRole(role)) {
    const defaults = {
      ...ALL_FALSE,
      canViewPlanning: true,
      canViewPhotos: true,
      canUploadFiles: true,
      canReviewDrawings: true,
    };
    return fullProjectScope ? defaults : { ...ALL_FALSE, canUploadFiles: true, canReviewDrawings: true };
  }

  if (isEstimatorRole(role)) {
    const defaults = {
      ...ALL_FALSE,
      canViewPlanning: true,
      canEditEstimate: true,
      canViewPhotos: true,
      canUploadFiles: true,
      canReviewDrawings: true,
      canLogTime: true,
      canViewTimeLog: true,
    };
    return fullProjectScope ? defaults : { ...ALL_FALSE, canUploadFiles: true, canReviewDrawings: true };
  }

  const defaults = {
    ...ALL_FALSE,
    canViewPlanning: true,
    canEditEstimate: true,
    canViewPhotos: true,
    canUploadFiles: true,
    canReviewDrawings: true,
    canLogTime: true,
    canViewTimeLog: true,
  };
  return fullProjectScope ? defaults : { ...ALL_FALSE, canUploadFiles: true, canReviewDrawings: true };
}

export function normalizeCollaboratorPermissions(raw = {}, context = {}) {
  const defaults = buildDefaultCollaboratorPermissions(context);
  const merged = { ...defaults };
  COLLABORATOR_PERMISSION_KEYS.forEach((key) => {
    if (typeof raw?.[key] === "boolean") {
      merged[key] = raw[key];
    }
  });
  if (merged.canEditScope && !merged.canViewPlanning && raw?.canViewPlanning !== false) {
    merged.canViewPlanning = true;
  }
  if (merged.canEditEstimate && !merged.canViewPlanning && raw?.canViewPlanning !== false) {
    merged.canViewPlanning = true;
  }
  if (merged.canPublishEstimate) {
    if (raw?.canViewPlanning !== false) merged.canViewPlanning = true;
    if (raw?.canEditEstimate !== false) merged.canEditEstimate = true;
  }
  if (merged.canLogTime && !merged.canViewTimeLog && raw?.canViewTimeLog !== false) {
    merged.canViewTimeLog = true;
  }
  if (
    merged.canManageProjectSpecifications
    && !merged.canViewProjectSpecifications
    && raw?.canViewProjectSpecifications !== false
  ) {
    merged.canViewProjectSpecifications = true;
  }
  if (!isFullProjectAccessScope(context.accessScope)) {
    FULL_PROJECT_REQUIRED_PERMISSION_KEYS.forEach((key) => {
      merged[key] = false;
    });
  }
  return merged;
}

export function resolveProjectCollaboratorPermissions({
  matchedCollaborator = null,
  viewerProjectPersona = "guest",
  isAdmin = false,
  project = null,
  authUid = "",
  authEmail = "",
  assignmentUidDoc = null,
  assignmentEmailDoc = null,
  assignmentFirstClientModules = false,
  clientAssignmentLoading = false,
} = {}) {
  return resolvePlanningCapabilities({
    matchedCollaborator,
    viewerProjectPersona,
    isAdmin: isAdmin || viewerProjectPersona === "admin",
    project,
    authUid,
    authEmail,
    assignmentUidDoc,
    assignmentEmailDoc,
    assignmentFirstClientModules,
    clientAssignmentLoading,
  });
}

export function buildPermissionDrivenProjectTabs(
  permissions = {},
  {
    project = null,
    isPastProject = false,
    isSharedFilesOnlyViewer = false,
    packageAvailable = false,
    isClientPersona = false,
    isContributorPersona = true,
    assignment = null,
    accountingPermissionsUiEnabled = null,
  } = {}
) {
  return buildVisibleProjectPortalTabs({
    permissions,
    project,
    isPastProject,
    isSharedFilesOnlyViewer,
    packageAvailable,
    isClientPersona,
    isContributorPersona,
    assignment,
    accountingPermissionsUiEnabled,
  });
}

export function filterPlanningSubtabs(subtabs = [], permissions = {}) {
  return subtabs.filter(([key]) => {
    if (key === "overview") return permissions.canViewPlanning;
    if (key === "scope" || key === "conditions") return permissions.canViewPlanning;
    if (key === "photos") return permissions.canViewPlanning && permissions.canViewPhotos;
    if (key === "drawing_estimate" || key === "project_estimate" || key === "estimate") {
      return permissions.canViewPlanning;
    }
    return true;
  });
}

export function permissionsUseSlimProjectNav(permissions = {}, { viewerProjectPersona = "guest" } = {}) {
  if (viewerProjectPersona === "client" || viewerProjectPersona === "admin") return false;
  return true;
}

function buildPersistedAssignmentPermissionAllowlist({ accountingPermissionsUiEnabled = null } = {}) {
  const keys = new Set(COLLABORATOR_PERMISSION_KEYS);
  CLIENT_PORTAL_MODULE_PERMISSION_KEY_LIST.forEach((key) => keys.add(key));
  keys.add("canEditSchedule");
  keys.add(CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_KEY);
  keys.add(CLIENT_SUPPLIER_PRODUCT_DECISION_PERMISSION_KEY);
  CLIENT_SAFE_INVOICE_PERMISSION_KEY_LIST.forEach((key) => keys.add(key));
  DESIGN_BOARD_PERMISSION_KEY_LIST.forEach((key) => keys.add(key));
  if (projectAccountingPermissionsUiEnabled(accountingPermissionsUiEnabled)) {
    ACCOUNTING_EXTENDED_PERMISSION_KEYS.forEach((key) => keys.add(key));
  }
  return keys;
}

function stripUnrecognizedPersistedPermissionKeys(
  permissions = {},
  { accountingPermissionsUiEnabled = null } = {}
) {
  const allowed = buildPersistedAssignmentPermissionAllowlist({ accountingPermissionsUiEnabled });
  return Object.fromEntries(
    Object.entries(permissions || {}).filter(([key]) => allowed.has(key))
  );
}

function finalizeCollaboratorPermissionsForSave(
  permissions = {},
  rawPermissions = {},
  { accountingPermissionsUiEnabled = null } = {}
) {
  const next = stripUnrecognizedPersistedPermissionKeys(permissions, {
    accountingPermissionsUiEnabled,
  });
  if (
    projectAccountingPermissionsUiEnabled(accountingPermissionsUiEnabled) &&
    hasExplicitInvoicePermissionKeys(rawPermissions)
  ) {
    delete next[LEGACY_INVOICE_PERMISSION_KEY];
  }
  return next;
}

function pickPersistedExtendedPermissionFlags(raw = {}, { accountingPermissionsUiEnabled = null } = {}) {
  if (!projectAccountingPermissionsUiEnabled(accountingPermissionsUiEnabled)) {
    return {};
  }
  return ACCOUNTING_EXTENDED_PERMISSION_KEYS.reduce((acc, key) => {
    if (typeof raw?.[key] === "boolean") acc[key] = raw[key];
    return acc;
  }, {});
}

export function serializeCollaboratorPermissionsForSave(permissions = {}, context = {}) {
  const normalized = normalizeCollaboratorPermissions(permissions, context);
  const withExtended = {
    ...normalized,
    ...pickPersistedExtendedPermissionFlags(permissions, context),
  };
  const withInvoicePermissions = normalizeInvoicePermissionsForAssignmentSave(withExtended, {
    accountingPermissionsUiEnabled: context.accountingPermissionsUiEnabled,
    rawPermissions: permissions,
  });
  const withBudgetPermissions = normalizeBudgetPermissionsForAssignmentSave(withInvoicePermissions, {
    accountingPermissionsUiEnabled: context.accountingPermissionsUiEnabled,
    rawPermissions: permissions,
  });
  const withSupplierPermissions = normalizeProjectSupplierPermissionsForAssignmentSave(
    withBudgetPermissions,
    { rawPermissions: permissions }
  );
  const withClientModulePermissions = normalizeClientPortalModulePermissionsForAssignmentSave(
    withSupplierPermissions,
    { rawPermissions: permissions }
  );
  const withSchedulePermissions = normalizeSchedulePermissionsForAssignmentSave(
    withClientModulePermissions,
    { rawPermissions: permissions, role: context.role }
  );
  const withSpecificationPermissions = normalizeSpecificationPermissionsForAssignmentSave(
    withSchedulePermissions,
    { rawPermissions: permissions }
  );
  const withDesignBoardPermissions = normalizeDesignBoardPermissionsForAssignmentSave(
    withSpecificationPermissions,
    { rawPermissions: permissions }
  );
  const withClientSafeInvoicePermissions = normalizeClientSafeInvoicePermissionsForAssignmentSave(
    withDesignBoardPermissions,
    { rawPermissions: permissions }
  );
  const gateFiltered = stripExtendedPermissionsForGateOffSave(withClientSafeInvoicePermissions, {
    accountingPermissionsUiEnabled: context.accountingPermissionsUiEnabled,
  });
  return finalizeCollaboratorPermissionsForSave(gateFiltered, permissions, {
    accountingPermissionsUiEnabled: context.accountingPermissionsUiEnabled,
  });
}

export function serializeCollaboratorRowPermissionsForSave(row = {}, options = {}) {
  const role = rawStr(row?.role || "reviewer").toLowerCase() || "reviewer";
  const accessScope = rawStr(row?.accessScope || "full_project").toLowerCase() || "full_project";
  return serializeCollaboratorPermissionsForSave(row?.permissions || {}, {
    role,
    accessScope,
    accountingPermissionsUiEnabled: options.accountingPermissionsUiEnabled,
  });
}

/** Matches AdminProjectDetail / firestore.rules assignment doc id sanitization. */
export function sanitizeProjectAssignmentKey(key) {
  return String(key ?? "").trim().replace(/[^a-zA-Z0-9_-]/g, "_");
}

export function buildCollaboratorUidAccessKeys(uid) {
  const exactUid = rawStr(uid);
  if (!exactUid) return [];
  const legacyLowerUid = exactUid.toLowerCase();
  return legacyLowerUid && legacyLowerUid !== exactUid ? [exactUid, legacyLowerUid] : [exactUid];
}

export function buildProjectAssignmentDocId(projectId, { uid = null, emailLower = null } = {}) {
  const pid = String(projectId ?? "").trim();
  const assignmentKey = rawStr(uid) || normalizeCollaboratorEmailLower(emailLower);
  if (!pid || !assignmentKey) return null;
  return `${pid}__${sanitizeProjectAssignmentKey(assignmentKey)}`;
}

export function enrichCollaboratorRowsForAccessRepair(rows = [], usersByUid = {}, usersByEmail = {}) {
  return (Array.isArray(rows) ? rows : []).map((row) => {
    const crmUid = rawStr(row?.uid);
    const emailLower = normalizeCollaboratorEmailLower(row?.emailLower || row?.email);
    const userData =
      (crmUid ? usersByUid[crmUid] || null : null) ||
      (emailLower ? usersByEmail[emailLower] || null : null);
    const userDocId = rawStr(userData?.id || userData?.uid || userData?.userId);
    const inferredAuthUid = userDocId && userDocId !== crmUid ? userDocId : "";
    const authUid = rawStr(userData?.authUid || row?.authUid || inferredAuthUid);
    const role = rawStr(row?.role || "reviewer").toLowerCase() || "reviewer";
    const accessScope = rawStr(row?.accessScope || "full_project").toLowerCase() || "full_project";
    const permissions = serializeCollaboratorRowPermissionsForSave(
      { ...row, role, accessScope },
      { accountingPermissionsUiEnabled: row?.accountingPermissionsUiEnabled }
    );
    return {
      ...row,
      uid: crmUid || null,
      authUid: authUid || null,
      emailLower: emailLower || null,
      role,
      accessScope,
      status: rawStr(row?.status || "active").toLowerCase() || "active",
      permissions,
    };
  });
}

function buildAssignmentPayload(row, projectId) {
  const accessScope = rawStr(row?.accessScope || "full_project").toLowerCase() || "full_project";
  return {
    projectId,
    userId: rawStr(row?.authUid || row?.uid) || null,
    email: row?.email || null,
    emailLower: row?.emailLower || null,
    displayName: row?.name || null,
    company: row?.company || null,
    role: row?.role,
    accessScope,
    status: row?.status,
    permissions: row?.permissions,
    portalEnabled: row?.status === "active",
  };
}

/**
 * Build the Firestore merge payload for one projectAssignments doc.
 * Existing assignments keep assignedAt/assignedBy; updates only touch audit update fields.
 */
export function buildProjectAssignmentSaveMergePayload({
  payload = {},
  existingAssignment = null,
  adminUid = null,
  assignedAt = null,
  updatedAt = null,
} = {}) {
  const isExisting = Boolean(existingAssignment);
  return {
    ...payload,
    ...(isExisting
      ? {
          assignedBy: existingAssignment?.assignedBy ?? null,
          assignedAt: existingAssignment?.assignedAt ?? null,
        }
      : {
          assignedBy: adminUid || null,
          assignedAt: assignedAt ?? null,
        }),
    updatedAt: updatedAt ?? null,
    updatedBy: adminUid || null,
  };
}

export function buildCollaboratorAssignmentRepairPlan(projectId, rows = []) {
  const pid = rawStr(projectId);
  const desiredIds = new Set();
  const assignments = [];
  const pushUidKeys = (keys, uid) => {
    const canonicalUid = rawStr(uid);
    buildCollaboratorUidAccessKeys(canonicalUid).forEach((accessUid) => {
      keys.push({ uid: accessUid, userId: canonicalUid });
    });
  };

  (Array.isArray(rows) ? rows : []).forEach((row, index) => {
    if (rawStr(row?.status).toLowerCase() === "removed") return;
    const payload = buildAssignmentPayload(row, pid);
    const keys = [];

    const emailLower = normalizeCollaboratorEmailLower(row?.emailLower || row?.email);
    const authUid = rawStr(row?.authUid);
    const crmUid = rawStr(row?.uid);

    if (emailLower) keys.push({ emailLower });
    if (authUid) pushUidKeys(keys, authUid);
    else if (crmUid) pushUidKeys(keys, crmUid);
    else if (!emailLower) pushUidKeys(keys, `manual_${index}`);

    if (crmUid && authUid && crmUid !== authUid) {
      pushUidKeys(keys, crmUid);
    }

    const seen = new Set();
    keys.forEach((key) => {
      const docId = buildProjectAssignmentDocId(pid, key);
      if (!docId || seen.has(docId)) return;
      seen.add(docId);
      desiredIds.add(docId);
      assignments.push({
        docId,
        payload: {
          ...payload,
          userId: key.uid ? rawStr(key.userId || key.uid) || null : rawStr(row?.authUid || row?.uid) || null,
        },
      });
    });
  });

  return { desiredIds, assignments };
}

export function buildCollaboratorAccessRepairSummary(rows = []) {
  const plan = buildCollaboratorAssignmentRepairPlan("project", rows);
  const { byUid, byEmail } = buildCollaboratorPermissionsDenormMaps(rows);
  return {
    assignmentDocCount: plan.assignments.length,
    denormUidKeys: Object.keys(byUid),
    denormEmailKeys: Object.keys(byEmail),
  };
}

export function buildCollaboratorAccessRepairWriteModel({ projectId = "", rows = [], usersByUid = {}, usersByEmail = {} } = {}) {
  const enrichedRows = enrichCollaboratorRowsForAccessRepair(rows, usersByUid, usersByEmail);
  const { byUid: collaboratorPermissionsByUid, byEmail: collaboratorPermissionsByEmail } =
    buildCollaboratorPermissionsDenormMaps(enrichedRows);
  const repairPlan = buildCollaboratorAssignmentRepairPlan(projectId, enrichedRows);
  return {
    rows: enrichedRows,
    collaboratorPermissionsByUid,
    collaboratorPermissionsByEmail,
    repairPlan,
    repairSummary: {
      assignmentDocCount: repairPlan.assignments.length,
      denormUidKeys: Object.keys(collaboratorPermissionsByUid),
      denormEmailKeys: Object.keys(collaboratorPermissionsByEmail),
    },
  };
}

/**
 * CL-409H / CL-409J — Mirror Firestore pm_can_publish_estimate (unit tests).
 */
export function resolveCanPublishEstimatePermission({
  isAdmin = false,
  assignmentUidPermissions = null,
  assignmentEmailPermissions = null,
  denormUidPermissions = null,
  denormEmailPermissions = null,
  project = null,
  authUid = "",
  authEmail = "",
} = {}) {
  const uid = String(authUid ?? "").trim();
  const em = String(authEmail ?? "").trim().toLowerCase();
  const projectForResolve =
    project ||
    (denormUidPermissions || denormEmailPermissions
      ? {
          collaboratorPermissionsByUid: denormUidPermissions && uid ? { [uid]: denormUidPermissions } : {},
          collaboratorPermissionsByEmail:
            denormEmailPermissions && em ? { [em]: denormEmailPermissions } : {},
        }
      : null);

  return resolveCanPublishEstimateCapability({
    isAdmin,
    project: projectForResolve,
    authUid: uid,
    authEmail: em,
    hasFullProjectAssignment: Boolean(assignmentUidPermissions || assignmentEmailPermissions),
    assignmentUidDoc: assignmentUidPermissions
      ? { status: "active", role: "collaborator", accessScope: "full_project", permissions: assignmentUidPermissions }
      : null,
    assignmentEmailDoc: assignmentEmailPermissions
      ? { status: "active", role: "collaborator", accessScope: "full_project", permissions: assignmentEmailPermissions }
      : null,
  });
}

/** CL-409A / CL-409I — Denormalized permission maps for Firestore rules (uid + authUid + email lookups). */
export function buildCollaboratorPermissionsDenormMaps(rows = []) {
  const byUid = {};
  const byEmail = {};
  const writeUidPermissions = (uid, permissions) => {
    buildCollaboratorUidAccessKeys(uid).forEach((accessUid) => {
      byUid[accessUid] = permissions;
    });
  };
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (safeStr(row?.status) === "removed") return;
    const permissions = serializeCollaboratorRowPermissionsForSave(row);
    const crmUid = rawStr(row?.uid);
    const authUid = rawStr(row?.authUid);
    const emailLower = normalizeCollaboratorEmailLower(row?.emailLower || row?.email);
    writeUidPermissions(crmUid, permissions);
    writeUidPermissions(authUid, permissions);
    if (emailLower) byEmail[emailLower] = permissions;
  });
  return { byUid, byEmail };
}

function permissionsMatch(left = {}, right = {}) {
  return COLLABORATOR_PERMISSION_KEYS.every((key) => Boolean(left[key]) === Boolean(right[key]));
}

export function buildPermissionPresetPermissions(presetKey, context = {}) {
  const key = safeStr(presetKey);
  if (key === COLLABORATOR_PERMISSION_PRESET_KEYS.CLIENT) {
    return normalizeCollaboratorPermissions(
      buildDefaultCollaboratorPermissions({ ...context, role: "client" }),
      { ...context, role: "client" }
    );
  }
  if (key === COLLABORATOR_PERMISSION_PRESET_KEYS.ESTIMATOR) {
    const estimatorContext = { ...context, role: "consultant" };
    return normalizeCollaboratorPermissions(
      {
        ...buildDefaultCollaboratorPermissions(estimatorContext),
        canEditScope: true,
      },
      estimatorContext
    );
  }
  if (key === COLLABORATOR_PERMISSION_PRESET_KEYS.DESIGNER) {
    return normalizeCollaboratorPermissions(
      buildDefaultCollaboratorPermissions({ ...context, role: "designer" }),
      { ...context, role: "designer" }
    );
  }
  if (key === COLLABORATOR_PERMISSION_PRESET_KEYS.TRADE) {
    return normalizeCollaboratorPermissions(
      buildDefaultCollaboratorPermissions({ ...context, role: "supplier" }),
      { ...context, role: "supplier" }
    );
  }
  if (key === COLLABORATOR_PERMISSION_PRESET_KEYS.PROJECT_ADMIN) {
    return normalizeCollaboratorPermissions(
      buildDefaultCollaboratorPermissions({ ...context, role: "project_admin" }),
      { ...context, role: "project_admin" }
    );
  }
  if (key === COLLABORATOR_PERMISSION_PRESET_KEYS.ACCOUNTING) {
    return buildAccountingAssignmentPreset().permissions;
  }
  return null;
}

export function detectPermissionPreset(permissions = {}, context = {}) {
  const normalized = normalizeCollaboratorPermissions(permissions, context);
  for (const presetKey of COLLABORATOR_PERMISSION_PRESET_ORDER) {
    if (presetKey === COLLABORATOR_PERMISSION_PRESET_KEYS.CUSTOM) continue;
    const presetPermissions = buildPermissionPresetPermissions(presetKey, context);
    if (presetPermissions && permissionsMatch(normalized, presetPermissions)) {
      return presetKey;
    }
  }
  return COLLABORATOR_PERMISSION_PRESET_KEYS.CUSTOM;
}

export { buildPlanningCapabilityContext, resolvePlanningCapabilities } from "./planningCapabilityResolver";

export function buildCollaboratorAccessSummaryLines(permissions = {}, context = {}) {
  const perms = normalizeCollaboratorPermissions(permissions, context);
  const lines = [];
  const fullProjectScope = isFullProjectAccessScope(context.accessScope);

  lines.push("Access scope limits what this person can reach. Permissions only apply inside that scope.");

  if (!fullProjectScope) {
    lines.push("Planning permissions require Full project access. This person stays limited to shared or scoped files.");
  }

  if (!perms.canViewPlanning) {
    lines.push("Cannot access the planning package on this project.");
    if (perms.canReviewDrawings) lines.push("Can review drawings in Drawings & review.");
    if (perms.canLogTime || perms.canViewTimeLog) lines.push("Can use the project time log.");
    if (perms.canManageInvoices) {
      lines.push(
        "Can create and edit invoice drafts for this project (publication remains Admin-only)."
      );
      if (!perms.canViewTimeLog) {
        lines.push(
          "Preparing invoices from Time Log also requires View time log — enable that permission separately."
        );
      }
    }
    if (!lines.length) lines.push("Limited project access — check individual permissions below.");
    return lines;
  }

  lines.push("Can view the planning package (scope, conditions, photos, and estimate workspace).");

  if (perms.canEditScope) {
    lines.push("Can add and edit scope areas and items directly.");
  } else if (perms.canEditEstimate) {
    lines.push("Can suggest scope items when estimating (direct scope edit is off).");
  }

  if (perms.canEditEstimate) {
    lines.push("Can edit draft planning estimate lines.");
  } else {
    lines.push("Cannot edit draft planning estimates.");
  }

  if (perms.canPublishEstimate) {
    lines.push("Can publish the planning estimate to the client.");
  }

  if (perms.canViewPhotos) {
    lines.push("Can view project photos in planning.");
  }
  if (perms.canUploadFiles) {
    lines.push("Can upload files for this project.");
  }
  if (perms.canReviewDrawings) {
    lines.push("Can access Drawings & review.");
  }
  if (perms.canLogTime) {
    lines.push("Can log time on this project.");
  } else if (perms.canViewTimeLog) {
    lines.push("Can view the project time log (read-only).");
  }
  if (perms.canManageCollaborators) {
    lines.push("Can manage project collaborators.");
  }

  return lines;
}
